import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'react-toastify'

import {
  DragDropFileProvider,
  DragDropFile,
  ErrorMessage,
  type FileWithStatus,
} from '@loadsmart/loadsmart-ui'
import type { Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import type EventLike from '@loadsmart/loadsmart-ui/dist/utils/types/EventLike'
import { Dialog, Field, Layout, TextField } from '@loadsmart/miranda-react'

import { mapToOptions, type Option } from 'core/domain/Option'
import { Icon } from 'core/ui/components/icon/Icon'

import { useConfigContext } from 'config/ui/ConfigContext'

import { DocumentType } from '../../domain/Document'
import { type Stop } from '../../domain/Shipment'
import { formatStopLabel } from '../../presenters/stopPresenter'
import { addFilesToUpload } from '../../use-cases/addFileToUpload'
import { useUploadDocument } from '../hooks/useUploadDocuments'
import { StyledSelect } from './UploadDocumentDialog.styles'

type UploadDocumentDialogProps = Readonly<{
  shipmentUUID: string
  open: boolean
  hide: () => void
  onUploadDocument: (shipmentUUID: string) => void
  isLTLorVLTL: boolean
  stops?: Array<Stop>
}>

type State = {
  documentType: DocumentType | ''
  totalInvoiced: string
  invoiceNumber: string
  filesToUpload: Array<FileWithStatus>
  validationError: string | undefined
  selectedStop?: Stop
}

function getDocumentTypeOptions(isLTLorVLTL: boolean) {
  const values = [
    { label: 'Invoice', value: DocumentType.Invoice },
    { label: 'Proof of Delivery', value: DocumentType.POD },
    { label: 'Proof of Pickup', value: DocumentType.ProofOfPickup },
  ]

  if (isLTLorVLTL) {
    values.push(
      { label: 'Weight Certificate', value: DocumentType.WeightCertificate },
      { label: 'Inspection Certificate', value: DocumentType.InspectionCertificate }
    )
  }

  return mapToOptions({
    values,
    getLabel: value => value.label,
    getValue: value => value.value,
  })
}

function getMatchingStops(documentType: DocumentType | '', stops: Array<Stop>): Array<Stop> {
  if (documentType === DocumentType.ProofOfPickup) {
    return stops.filter(stop => stop.stopType === 'pickup')
  }

  if (documentType === DocumentType.POD) {
    return stops.filter(stop => stop.stopType === 'delivery')
  }

  return []
}

function getResolvedStop(
  documentType: DocumentType | '',
  matchingStops: Array<Stop>,
  selectedStop: Stop | undefined
): Stop | undefined {
  if (documentType !== DocumentType.POD && documentType !== DocumentType.ProofOfPickup) {
    return undefined
  }

  if (matchingStops.length === 1) {
    return matchingStops[0]
  }

  if (selectedStop && matchingStops.some(stop => stop.id === selectedStop?.id)) {
    return selectedStop
  }

  return undefined
}

export default function UploadDocumentDialog({
  shipmentUUID,
  open,
  hide,
  onUploadDocument,
  isLTLorVLTL,
  stops = [],
}: UploadDocumentDialogProps) {
  const configs = useConfigContext()
  const enableDocumentStopSelection = configs.enableDocumentStopSelection.toBoolean()
  const documentTypeOptions = getDocumentTypeOptions(isLTLorVLTL)

  const [dialogState, setDialogState] = useState<State>({
    documentType: '',
    totalInvoiced: '',
    invoiceNumber: '',
    filesToUpload: [],
    validationError: undefined,
  })

  const { uploadDocuments, reset, isPending, isDone, hasError } = useUploadDocument()

  const matchingStops = useMemo(
    () => getMatchingStops(dialogState.documentType, stops),
    [dialogState.documentType, stops]
  )

  const resolvedStop = useMemo(
    () => getResolvedStop(dialogState.documentType, matchingStops, dialogState.selectedStop),
    [dialogState.documentType, matchingStops, dialogState.selectedStop]
  )

  const isInvoice = dialogState.documentType === DocumentType.Invoice

  const shouldShowStopSelector =
    dialogState.documentType === DocumentType.POD ||
    dialogState.documentType === DocumentType.ProofOfPickup

  const requiresStop = enableDocumentStopSelection && shouldShowStopSelector && !resolvedStop

  const disableUploadButton =
    !dialogState.documentType ||
    isPending ||
    (isInvoice && (!dialogState.totalInvoiced || !dialogState.invoiceNumber)) ||
    requiresStop ||
    dialogState.filesToUpload.length === 0

  const handleDocumentTypeChange = useCallback(
    (event: EventLike<Selectable | Selectable[] | null>) => {
      const option = event?.target?.value as Option<DocumentType> | null
      const documentType = option?.value ?? ''

      const newMatchingStops = getMatchingStops(documentType, stops)

      const autoSelectedStop = getResolvedStop(documentType, newMatchingStops, undefined)

      setDialogState(prev => ({
        ...prev,
        documentType,
        selectedStop: autoSelectedStop,
      }))
    },
    [stops]
  )

  const handleStopChange = useCallback(
    (event: EventLike<Selectable | Selectable[] | null>) => {
      const stopOption = event?.target?.value as Option<string | number> | null
      const stopId = stopOption === null ? undefined : stopOption.value
      const selectedStop =
        stopId === undefined ? undefined : matchingStops.find(stop => stop.id === stopId)
      setDialogState(prev => ({ ...prev, selectedStop }))
    },
    [matchingStops]
  )

  const handleAddFiles = useCallback((files: File[]) => {
    try {
      const filesWithStatus = addFilesToUpload(files)
      setDialogState(prev => ({
        ...prev,
        filesToUpload: filesWithStatus,
        validationError: undefined,
      }))
    } catch (error) {
      if (error instanceof Error) {
        const message = error.message
        setDialogState(prev => ({ ...prev, validationError: message }))
      }
    }
  }, [])

  const handleRemoveFiles = useCallback((newFileList: FileWithStatus[]) => {
    setDialogState(prev => ({ ...prev, filesToUpload: newFileList, validationError: undefined }))
  }, [])

  const handleUploadFiles = useCallback(() => {
    const file = dialogState.filesToUpload[0].file
    const { documentType, totalInvoiced, invoiceNumber } = dialogState

    if (
      documentType === DocumentType.POD ||
      documentType === DocumentType.ProofOfPickup ||
      documentType === DocumentType.WeightCertificate ||
      documentType === DocumentType.InspectionCertificate
    ) {
      uploadDocuments({
        documentType,
        file,
        shipmentUUID,
        ...(enableDocumentStopSelection && {
          stop: resolvedStop
            ? { id: resolvedStop.id, stopIndex: resolvedStop.stopIndex }
            : undefined,
        }),
      })
    } else if (documentType === DocumentType.Invoice) {
      uploadDocuments({
        documentType,
        file,
        shipmentUUID,
        total: Number(totalInvoiced),
        number: invoiceNumber,
      })
    }
  }, [dialogState, shipmentUUID, uploadDocuments, resolvedStop, enableDocumentStopSelection])

  const resetDialog = useCallback(() => {
    setDialogState({
      documentType: '',
      totalInvoiced: '',
      invoiceNumber: '',
      filesToUpload: [],
      validationError: undefined,
    })
    reset()
    hide()
  }, [hide, reset])

  useEffect(() => {
    if (isDone) {
      onUploadDocument(dialogState.documentType)
      resetDialog()
      toast.success('Document sent successfully')
    }
  }, [dialogState, isDone, resetDialog, onUploadDocument])

  useEffect(() => {
    if (hasError) {
      resetDialog()
      toast.error('Failed to send document. Please, try again')
    }
  }, [hasError, resetDialog])

  const stopOptions = useMemo(() => {
    return mapToOptions({
      values: matchingStops,
      getLabel: formatStopLabel,
      getValue: stop => stop.id,
    })
  }, [matchingStops])

  const matchingStop = useMemo(
    () => stopOptions.find(option => option.value === dialogState.selectedStop?.id),
    [stopOptions, dialogState.selectedStop]
  )

  useEffect(() => {
    if (shouldShowStopSelector && dialogState.selectedStop && !matchingStop) {
      setDialogState(prev => ({ ...prev, selectedStop: undefined }))
    }
  }, [shouldShowStopSelector, matchingStops, dialogState.selectedStop, matchingStop])
  return createPortal(
    <Dialog open={open} size="medium" data-testid="upload-dialog-document">
      <Dialog.Header>Upload Document</Dialog.Header>
      <Dialog.Close onClick={resetDialog} />
      <Dialog.Body>
        <Layout.Stack>
          <Field required>
            <Field.Label>Document type</Field.Label>
            <StyledSelect
              name="document-type-select"
              placeholder="Select type of document"
              options={documentTypeOptions}
              hideClear
              onChange={handleDocumentTypeChange}
              value={documentTypeOptions.find(option => option.value === dialogState.documentType)}
            />
          </Field>
          {shouldShowStopSelector && enableDocumentStopSelection && (
            <Field required>
              <Field.Label>Stop</Field.Label>
              <StyledSelect
                name="stop-select"
                placeholder="Select stop"
                options={stopOptions}
                hideClear
                onChange={handleStopChange}
                value={matchingStop}
              />
            </Field>
          )}
          {isInvoice && (
            <>
              <Field required>
                <Field.Label id="invoice-number">Invoice Number</Field.Label>
                <TextField
                  aria-labelledby="invoice-number"
                  placeholder="Enter invoice number"
                  type="text"
                  value={dialogState.invoiceNumber}
                  onChange={event =>
                    setDialogState(prev => ({ ...prev, invoiceNumber: event.target.value }))
                  }
                  data-testid="invoice-number-input"
                />
              </Field>
              <Field required>
                <Field.Label id="total-invoiced">Rate</Field.Label>
                <TextField
                  aria-labelledby="total-invoiced"
                  leading="$ "
                  placeholder="00.00"
                  type="text"
                  value={dialogState.totalInvoiced}
                  onChange={event =>
                    setDialogState(prev => ({ ...prev, totalInvoiced: event.target.value }))
                  }
                  data-testid="total-invoiced-input"
                />
              </Field>
            </>
          )}
          <DragDropFileProvider
            fileList={dialogState.filesToUpload}
            onFileRemoved={handleRemoveFiles}
            onFilesAdded={handleAddFiles}
          >
            <DragDropFile.Wrapper>
              <DragDropFile.DropZone
                multiple={false}
                hint="You can attach files: pdf, jpg, png with size limit of 10mb each"
                accept={['application/pdf', 'image/jpeg', 'image/png']}
                error={!!dialogState.validationError}
              />
              <ErrorMessage>{dialogState.validationError}</ErrorMessage>
              <DragDropFile.FileList />
            </DragDropFile.Wrapper>
          </DragDropFileProvider>
        </Layout.Stack>
      </Dialog.Body>
      <Dialog.Actions>
        <Dialog.ActionSecondary onClick={resetDialog}>Cancel</Dialog.ActionSecondary>
        <Dialog.ActionPrimary
          onClick={handleUploadFiles}
          disabled={disableUploadButton}
          data-testid="upload-confirm-btn"
        >
          <Icon name="upload" /> Upload
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </Dialog>,
    document.body,
    'upload-document-dialog-root'
  )
}
